import { useEffect, useRef, useState, useCallback } from 'react';

interface UseSwipeableTabsOptions {
  currentTab: string;
  tabs: string[];
  onTabChange: (tab: string) => void;
  enabled?: boolean;
  threshold?: number; // Percentage of container width to trigger tab change (0-1)
}

/**
 * Custom hook to enable swipeable tabs with smooth animation on mobile devices.
 * The content follows the finger during swipe and animates to the next/previous tab.
 */
export function useSwipeableTabs({
  currentTab,
  tabs,
  onTabChange,
  enabled = true,
  threshold = 0.25, // 25% of width triggers change
}: UseSwipeableTabsOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const containerWidth = useRef<number>(0);
  const isHorizontalSwipe = useRef<boolean>(false);
  const swipeStarted = useRef<boolean>(false);

  // Calculate current tab index
  const currentIndex = tabs.indexOf(currentTab);

  const resetSwipe = useCallback(() => {
    touchStartX.current = null;
    touchStartY.current = null;
    isHorizontalSwipe.current = false;
    swipeStarted.current = false;
    setIsDragging(false);
    setDragOffset(0);
  }, []);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      containerWidth.current = container.offsetWidth;
      isHorizontalSwipe.current = false;
      swipeStarted.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - touchStartX.current;
      const deltaY = currentY - touchStartY.current;

      // Determine swipe direction on first significant movement
      if (!swipeStarted.current) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (absX > 5 || absY > 5) {
          swipeStarted.current = true;
          isHorizontalSwipe.current = absX > absY * 1.5; // Require more horizontal than vertical
        }
      }

      // Check if touch is on a horizontally scrollable element
      const target = e.target as HTMLElement;
      const scrollableElement = target.closest('[class*="overflow-x-auto"], table, [class*="overflow-auto"]');

      if (scrollableElement) {
        const canScrollHorizontally = scrollableElement.scrollWidth > scrollableElement.clientWidth;

        // If the element can scroll horizontally, only allow tab swipe at edges
        if (canScrollHorizontally && isHorizontalSwipe.current) {
          const scrollLeft = scrollableElement.scrollLeft;
          const isAtLeftEdge = scrollLeft === 0;
          const isAtRightEdge = scrollLeft + scrollableElement.clientWidth >= scrollableElement.scrollWidth - 1;

          // Only allow tab swipe if:
          // - Swiping right (deltaX > 0) AND content is scrolled fully to LEFT (scrollLeft === 0)
          // - Swiping left (deltaX < 0) AND content is scrolled fully to RIGHT (at right edge)
          // Otherwise, prevent tab swipe and let table scroll
          const shouldPreventTabSwipe =
            (deltaX > 0 && !isAtLeftEdge) || // Swiping right but not at left edge
            (deltaX < 0 && !isAtRightEdge); // Swiping left but not at right edge

          if (shouldPreventTabSwipe) {
            // Prevent tab swipe, allow table to scroll
            resetSwipe();
            return;
          }
        }
      }

      // If horizontal swipe, prevent default scroll and update position
      if (isHorizontalSwipe.current) {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from bubbling to parent SwipeableTabs
        setIsDragging(true);

        // Prevent swiping beyond first/last tab with resistance
        const isFirstTab = currentIndex === 0;
        const isLastTab = currentIndex === tabs.length - 1;

        if ((isFirstTab && deltaX > 0) || (isLastTab && deltaX < 0)) {
          // Add resistance at edges (rubber band effect)
          const resistance = 0.25;
          const maxDrag = 100; // Maximum pixels to drag at edge
          const resistedDelta = Math.sign(deltaX) * Math.min(Math.abs(deltaX) * resistance, maxDrag);
          setDragOffset(resistedDelta);
        } else {
          setDragOffset(deltaX);
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isHorizontalSwipe.current || touchStartX.current === null || !containerWidth.current) {
        resetSwipe();
        return;
      }

      const swipeDistance = dragOffset;
      const swipePercentage = Math.abs(swipeDistance) / containerWidth.current;

      // Determine if we should change tabs
      const shouldChange = swipePercentage > threshold;

      if (shouldChange) {
        if (swipeDistance > 0 && currentIndex > 0) {
          // Swiped right - go to previous tab
          onTabChange(tabs[currentIndex - 1]);
        } else if (swipeDistance < 0 && currentIndex < tabs.length - 1) {
          // Swiped left - go to next tab
          onTabChange(tabs[currentIndex + 1]);
        }
      }

      resetSwipe();
    };

    const handleTouchCancel = () => {
      resetSwipe();
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [enabled, currentTab, tabs, onTabChange, threshold, dragOffset, currentIndex, resetSwipe]);

  // Calculate base position (current tab) in percentage
  // Each tab takes 100% width + 1rem margin (except last tab)
  // Use percentage-based calculation that works regardless of containerWidth
  const marginInPx = 16; // 1rem = 16px

  // Get container width - use ref if available, otherwise measure directly
  const getContainerWidth = () => {
    if (containerWidth.current > 0) return containerWidth.current;
    if (containerRef.current) return containerRef.current.offsetWidth;
    return 0;
  };

  const containerWidthValue = getContainerWidth();

  // Calculate position: each tab is 100% + margin
  // For tab 0: translateX(0%)
  // For tab 1: translateX(-(100% + margin))
  // For tab N: translateX(-(N * (100% + margin)))
  let baseTranslateX = 0;
  if (containerWidthValue > 0) {
    // Calculate offset in pixels, then convert to percentage
    const offsetInPx = currentIndex * (containerWidthValue + marginInPx);
    baseTranslateX = -(offsetInPx / containerWidthValue) * 100;
  } else {
    // Fallback: simple percentage calculation (assumes margin is ~4.4% of width on mobile)
    baseTranslateX = -(currentIndex * 104.4);
  }

  // Calculate drag offset in percentage
  const dragPercentage = containerWidthValue > 0 ? (dragOffset / containerWidthValue) * 100 : 0;

  // Final position = base position + drag offset
  const finalTranslateX = baseTranslateX + dragPercentage;

  return {
    containerRef,
    style: {
      transform: `translateX(${finalTranslateX}%)`,
      transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      willChange: isDragging ? 'transform' : 'auto',
    },
    isDragging,
  };
}
